/**
 * ResetPasswordPage — consumes ?token=… from the reset email link and
 * lets the user set a new password. On success we show a confirmation
 * and route to /login (the backend has already invalidated the token
 * and any active sessions for this user).
 */
import React, { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight, Loader2, Eye, EyeOff, CheckCircle2, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError } from "../lib/api";
import Wordmark from "../components/Wordmark";

const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/30f41ab2-b4a1-4f65-814e-99ce123f0f30/images/a7d60764e461dd70a1d1703344b38d69ea925fa140e7d762cecedddfa9a8a676.png";

export default function ResetPasswordPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const minLen = 8;
  const lengthOk = next.length >= minLen;
  const matches = next.length > 0 && next === confirm;
  const canSubmit = !!token && lengthOk && matches && !busy;
  const missingToken = useMemo(() => !token, [token]);

  if (user) return <Navigate to="/settings/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await apiClient.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      toast.success(
        lang === "ar"
          ? "تم إعادة تعيين كلمة المرور بنجاح."
          : "Your password has been reset."
      );
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) ||
        (lang === "ar" ? "تعذّرت إعادة التعيين" : "Failed to reset password")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12"
      data-testid="reset-password-page"
    >
      <div className="grid lg:grid-cols-2 max-w-5xl w-full glass rounded-3xl overflow-hidden">
        <div className="hidden lg:block relative">
          <img
            src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1200&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b3d91]/40 via-[#1a1a1a]/50 to-[#1a1a1a]/90" />
          <div className="relative h-full p-10 flex flex-col justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="" className="h-10 w-auto" aria-hidden />
              <Wordmark size="lg" />
            </div>
            <div>
              <div className="overline mb-3">
                <ShieldCheck className="w-4 h-4 inline-block me-1" />
                {lang === "ar" ? "أمان الحساب" : "Account security"}
              </div>
              <h2 className="font-display text-3xl tracking-tight font-medium leading-tight">
                {lang === "ar"
                  ? "اختر كلمة مرور جديدة قوية."
                  : "Choose a new, strong password."}
              </h2>
            </div>
          </div>
        </div>

        <div className="p-8 lg:p-12">
          <div className="overline mb-2">
            {lang === "ar" ? "إعادة تعيين" : "Reset"}
          </div>
          <h1 className="font-display text-3xl tracking-tight font-medium mb-2">
            {lang === "ar" ? "تعيين كلمة مرور جديدة" : "Set a new password"}
          </h1>
          <p className="text-[#6b6b6b] mb-8">
            {lang === "ar"
              ? "أدخل كلمة مرور جديدة لا تقل عن 8 أحرف."
              : "Enter a new password of at least 8 characters."}
          </p>

          {missingToken ? (
            <div
              className="rounded-xl border border-red-200 bg-red-50 p-5"
              data-testid="reset-missing-token"
            >
              <div className="text-[13px] text-[#b91c1c] leading-relaxed">
                {lang === "ar"
                  ? "الرابط غير مكتمل. افتح رابط إعادة التعيين من بريدك الإلكتروني كاملاً."
                  : "This link is incomplete. Open the reset link from your email in full."}
              </div>
              <div className="mt-4">
                <Link
                  to="/forgot-password"
                  className="btn-ghost text-[13px]"
                  data-testid="request-new-link"
                >
                  {lang === "ar" ? "طلب رابط جديد" : "Request a new link"}
                </Link>
              </div>
            </div>
          ) : done ? (
            <div
              className="rounded-xl border border-[#a8893e]/25 bg-[#fbf7ec] p-5"
              data-testid="reset-success"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#0f7c3a] mt-0.5" />
                <div className="text-[13px] text-[#3a3a3a] leading-relaxed">
                  {lang === "ar"
                    ? "تم تحديث كلمة المرور. سنقوم بتوجيهك لتسجيل الدخول بكلمة المرور الجديدة."
                    : "Your password has been updated. Redirecting you to sign in with the new password."}
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="reset-password-form" autoComplete="off">
              <div>
                <label className="text-xs text-[#6b6b6b]">
                  {lang === "ar" ? "كلمة المرور الجديدة" : "New password"}
                </label>
                <div className="relative mt-1">
                  <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b6b6b]" />
                  <input
                    className="input-dark ps-10 pe-10"
                    type={showNext ? "text" : "password"}
                    required
                    minLength={minLen}
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    autoComplete="new-password"
                    autoFocus
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNext((s) => !s)}
                    className="absolute end-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center hover:bg-[#0a0a0a]/5 transition-colors"
                    aria-label={lang === "ar" ? "إظهار/إخفاء" : "Show/Hide"}
                    data-testid="reset-toggle-new"
                  >
                    {showNext
                      ? <EyeOff className="w-4 h-4 text-[#6b6b6b]" />
                      : <Eye className="w-4 h-4 text-[#6b6b6b]" />}
                  </button>
                </div>
                <div className={`text-[11px] mt-1 ${lengthOk ? "text-[#0f7c3a]" : "text-[#8a8a8a]"}`}>
                  {lang === "ar" ? `الحد الأدنى ${minLen} أحرف` : `Minimum ${minLen} characters`}
                </div>
              </div>

              <div>
                <label className="text-xs text-[#6b6b6b]">
                  {lang === "ar" ? "تأكيد كلمة المرور الجديدة" : "Confirm new password"}
                </label>
                <div className="relative mt-1">
                  <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b6b6b]" />
                  <input
                    className="input-dark ps-10"
                    type={showNext ? "text" : "password"}
                    required
                    minLength={minLen}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    data-testid="reset-confirm-password"
                  />
                </div>
                {confirm.length > 0 && !matches && (
                  <div className="text-[11px] mt-1 text-[#b91c1c]" data-testid="reset-mismatch">
                    {lang === "ar" ? "كلمتا المرور غير متطابقتين." : "Passwords don't match."}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary w-full justify-center"
                disabled={!canSubmit}
                data-testid="reset-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {busy
                  ? (lang === "ar" ? "جارٍ التعيين…" : "Resetting…")
                  : (lang === "ar" ? "تعيين كلمة المرور" : "Set password")}
              </button>
            </form>
          )}

          <p className="text-sm text-[#6b6b6b] mt-6">
            {lang === "ar" ? "لديك حساب؟" : "Already have access?"}{" "}
            <Link to="/login" className="text-[#8a6f2e] hover:text-[#c9a55c]" data-testid="switch-to-login">
              {lang === "ar" ? "تسجيل الدخول" : "Sign in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
